import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
} from "@mui/material";
import { useState } from "react";
import { SectionRevisions } from "../utils/templateDrafts";
import { newRevMessage } from "../utils/templateMessages";

type Props = {
	revisions: SectionRevisions;
	currentRev: number | null;
	sectionName: string;
	inspectionCount: number;
	onClose: () => void;
	onUpdateThisRevision: () => void;
	onCreateRevision: (rev: number) => void;
};

export const RevisionPromptDialog = ({
	revisions,
	currentRev,
	sectionName,
	inspectionCount,
	onClose,
	onUpdateThisRevision,
	onCreateRevision,
}: Props) => {
	const [rev, setRev] = useState<number | null>(revisions.nextRev);

	const revError = newRevMessage(revisions, rev);

	return (
		<Dialog open onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Save as a new revision?</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ mb: 2 }}>
					{inspectionCount > 0
						? `${inspectionCount} ${
								inspectionCount === 1 ? "inspection was" : "inspections were"
							} already taken under Rev ${currentRev}. Saving over it changes the sheet those readings were taken against; a new revision leaves them as they were read and takes over on the day it becomes effective.`
						: `Rev ${currentRev} is what ${sectionName} is inspected under. Saving over it rewrites that revision; a new revision leaves it as it stands and takes over on the day it becomes effective.`}
				</DialogContentText>
				<TextField
					label="New rev"
					type="number"
					value={rev ?? ""}
					fullWidth
					size="small"
					autoFocus
					autoComplete="off"
					error={revError !== null}
					helperText={revError ?? undefined}
					slotProps={{ htmlInput: { min: 0, step: 1 } }}
					onChange={(event) => {
						const value = Number.parseInt(event.target.value, 10);
						setRev(Number.isNaN(value) || value < 0 ? null : value);
					}}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button color="inherit" onClick={onClose}>
					Cancel
				</Button>
				<Button color="warning" onClick={onUpdateThisRevision}>
					Update this revision
				</Button>
				<Button
					variant="contained"
					disabled={rev === null || revError !== null}
					onClick={() => rev !== null && onCreateRevision(rev)}
				>
					Create revision
				</Button>
			</DialogActions>
		</Dialog>
	);
};
